import React, { useState, useEffect } from 'react'
import FitToPageButton from './FitToPageButton'
import { isLikelySvg } from '../utils/svgUtils'

/**
 * SVG Element Properties — properties panel for first-class `type:"svg"` items
 * that carry inline `svgContent`.
 *
 * Provides the basic "SVG editor": size, rotation, opacity, aspect-fit toggle,
 * optional recolor (single fill/stroke override that preserves-or-replaces the
 * SVG's native palette), and a raw-markup editor (textarea) for direct edits.
 */
const SvgElementProperties = ({
  element,
  selectedElement,
  updateElementProperty,
  currentPage,
}) => {
  const [showCode, setShowCode] = useState(false)
  const [draft, setDraft] = useState(element?.svgContent || '')
  const [codeError, setCodeError] = useState('')

  // Keep the draft in sync when the selected element changes.
  useEffect(() => {
    setDraft(element?.svgContent || '')
    setCodeError('')
  }, [selectedElement, element?.svgContent])

  if (!element || element.type !== 'svg') {
    return null
  }

  const applyCode = () => {
    if (!isLikelySvg(draft)) {
      setCodeError('Content must be a valid <svg>...</svg> document.')
      return
    }
    setCodeError('')
    updateElementProperty(selectedElement, 'svgContent', draft)
  }

  const recolor = !!element.recolorSvg

  return (
    <div className="flex flex-col gap-2 w-full">
      {/* Primary row: size / rotation / opacity / fit */}
      <div className="flex items-center space-x-4 flex-wrap gap-y-2">
        {/* Width */}
        <div className="flex items-center space-x-2">
          <label className="text-sm text-gray-600">Width:</label>
          <input
            type="number"
            value={Math.round(element.width || 100)}
            onChange={(e) => updateElementProperty(selectedElement, 'width', parseInt(e.target.value) || 1)}
            className="border border-gray-300 rounded px-3 py-1.5 text-sm w-20 focus:outline-none focus:ring-2 focus:ring-blue-500"
            min="1"
            max="4000"
          />
        </div>

        {/* Height */}
        <div className="flex items-center space-x-2">
          <label className="text-sm text-gray-600">Height:</label>
          <input
            type="number"
            value={Math.round(element.height || 100)}
            onChange={(e) => updateElementProperty(selectedElement, 'height', parseInt(e.target.value) || 1)}
            className="border border-gray-300 rounded px-3 py-1.5 text-sm w-20 focus:outline-none focus:ring-2 focus:ring-blue-500"
            min="1"
            max="4000"
          />
        </div>

        {/* Rotation */}
        <div className="flex items-center space-x-2">
          <label className="text-sm text-gray-600">Rotation:</label>
          <input
            type="number"
            value={Math.round(element.rotation || 0)}
            onChange={(e) => updateElementProperty(selectedElement, 'rotation', parseInt(e.target.value) || 0)}
            className="border border-gray-300 rounded px-3 py-1.5 text-sm w-20 focus:outline-none focus:ring-2 focus:ring-blue-500"
            min="-360"
            max="360"
          />
          <span className="text-xs text-gray-400">°</span>
        </div>

        {/* Opacity */}
        <div className="flex items-center space-x-2">
          <label className="text-sm text-gray-600">Opacity:</label>
          <input
            type="range"
            min="0"
            max="1"
            step="0.05"
            value={element.opacity ?? 1}
            onChange={(e) => updateElementProperty(selectedElement, 'opacity', parseFloat(e.target.value))}
            className="w-24"
          />
          <span className="text-xs text-gray-500 w-8 text-right">
            {Math.round((element.opacity ?? 1) * 100)}%
          </span>
        </div>

        {/* Aspect fit toggle (stretch vs. preserve aspect) */}
        <label className="flex items-center space-x-2 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={!!element.stretch}
            onChange={(e) => updateElementProperty(selectedElement, 'stretch', e.target.checked)}
            className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
          />
          <span className="text-sm text-gray-600">Stretch to box</span>
        </label>

        {currentPage && (
          <FitToPageButton
            element={element}
            selectedElement={selectedElement}
            updateElementProperty={updateElementProperty}
            currentPage={currentPage}
          />
        )}

        {/* Edit-code toggle */}
        <button
          onClick={() => setShowCode((v) => !v)}
          className={`px-3 py-1.5 text-sm rounded border ${showCode ? 'bg-blue-600 text-white border-blue-600' : 'border-gray-300 hover:bg-gray-50 text-gray-700'}`}
          title="Edit the raw SVG markup"
        >
          {showCode ? 'Close code' : 'Edit code'}
        </button>
      </div>

      {/* Recolor row */}
      <div className="flex items-center space-x-4 flex-wrap gap-y-2">
        <label className="flex items-center space-x-2 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={recolor}
            onChange={(e) => updateElementProperty(selectedElement, 'recolorSvg', e.target.checked)}
            className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
          />
          <span className="text-sm text-gray-600">Recolor (override)</span>
        </label>

        {recolor && (
          <>
            <div className="flex items-center space-x-2">
              <label className="text-sm text-gray-600">Fill:</label>
              <input
                type="color"
                value={element.fill && element.fill !== 'transparent' ? element.fill : '#000000'}
                onChange={(e) => updateElementProperty(selectedElement, 'fill', e.target.value)}
                className="w-8 h-8 border border-gray-300 rounded cursor-pointer"
              />
              <button
                onClick={() => updateElementProperty(selectedElement, 'fill', 'none')}
                className="px-2 py-1 text-xs border border-gray-300 rounded hover:bg-gray-50"
              >
                None
              </button>
            </div>

            <div className="flex items-center space-x-2">
              <label className="text-sm text-gray-600">Stroke:</label>
              <input
                type="color"
                value={element.stroke && element.stroke !== 'none' ? element.stroke : '#000000'}
                onChange={(e) => updateElementProperty(selectedElement, 'stroke', e.target.value)}
                className="w-8 h-8 border border-gray-300 rounded cursor-pointer"
              />
              <button
                onClick={() => updateElementProperty(selectedElement, 'stroke', 'none')}
                className="px-2 py-1 text-xs border border-gray-300 rounded hover:bg-gray-50"
              >
                None
              </button>
            </div>

            <div className="flex items-center space-x-2">
              <label className="text-sm text-gray-600">Stroke W:</label>
              <input
                type="number"
                value={element.strokeWidth || 1}
                onChange={(e) => updateElementProperty(selectedElement, 'strokeWidth', parseFloat(e.target.value) || 0)}
                className="border border-gray-300 rounded px-3 py-1.5 text-sm w-16 focus:outline-none focus:ring-2 focus:ring-blue-500"
                min="0"
                max="100"
                step="0.5"
              />
            </div>
            <span className="text-xs text-gray-400">
              Overrides every fill/stroke with one color. Leave off to keep the SVG's own colors.
            </span>
          </>
        )}
      </div>

      {/* Raw-markup editor */}
      {showCode && (
        <div className="flex flex-col gap-2 w-full">
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            spellCheck={false}
            rows={8}
            className="w-full font-mono text-xs border border-gray-300 rounded p-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="<svg ...>...</svg>"
          />
          {codeError && <div className="text-xs text-red-600">{codeError}</div>}
          <div className="flex items-center gap-2">
            <button
              onClick={applyCode}
              className="px-3 py-1.5 text-sm rounded bg-blue-600 text-white hover:bg-blue-700"
            >
              Apply code
            </button>
            <button
              onClick={() => { setDraft(element.svgContent || ''); setCodeError('') }}
              className="px-3 py-1.5 text-sm rounded border border-gray-300 hover:bg-gray-50 text-gray-700"
            >
              Reset
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default SvgElementProperties
